import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  GitCompare,
  Layers,
  XCircle,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import { cn } from "@/lib/utils";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  showToast,
} from "@/components/ui";
import { ComparisonTable, ContextBar } from "@/components/catalog";
import { useCompareMarketplaces } from "@/hooks/useCompareMarketplaces";
import { formatProductType } from "@/lib/formatProductType";

const tabs = [
  { key: "category", label: "Category Coverage" },
  { key: "attribute", label: "Attribute Coverage" },
  { key: "sku", label: "Product/SKU Coverage" },
  { key: "health", label: "Mapping Health" },
] as const;

export default function CompareMarketplaces() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] =
    useState<(typeof tabs)[number]["key"]>("category");
  const {
    marketplaces,
    sourceMarketplace,
    setSourceMarketplace,
    categoryRows,
    summary,
    isLoading,
    isError,
  } = useCompareMarketplaces();

  const handleCompare = () => {
    showToast.info(
      "Comparing...",
      `Comparing ${sourceMarketplace} against Canonical model`,
    );
  };

  const handleReview = (row: Record<string, unknown>) => {
    const label =
      row.sourceCategory ?? row.sourceAttr ?? row.sourceSku ?? "selected item";
    showToast.info("Review Item", `Opening review for: ${String(label)}`);
  };

  const handleEdit = (row: Record<string, unknown>) => {
    const marketplace =
      typeof row.marketplace === "string" && row.marketplace.trim()
        ? row.marketplace.trim()
        : sourceMarketplace;
    const productType =
      typeof row.productType === "string" && row.productType.trim()
        ? row.productType.trim()
        : typeof row.sourceCategory === "string"
          ? row.sourceCategory.trim()
          : "";

    if (!marketplace || !productType) {
      showToast.error(
        "Cannot edit mapping",
        "This row is missing marketplace or product type details.",
      );
      return;
    }

    const params = new URLSearchParams({
      mode: "edit",
      productType,
      productTypeLabel: formatProductType(productType) || productType,
    });
    if (typeof row.canonicalCategoryId === "string") {
      params.set("canonicalCategoryId", row.canonicalCategoryId);
    }
    if (typeof row.canonicalCategoryName === "string") {
      params.set("canonicalCategoryName", row.canonicalCategoryName);
    }

    navigate(`/catalog/sync/${marketplace.toLowerCase()}?${params.toString()}`);
  };

  return (
    <DashboardLayout activePath="/catalog/compare">
      <PageHeader
        title="Compare Marketplaces"
        subtitle="Compare marketplace catalog data against the Canonical model to identify mapping gaps"
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Compare Marketplaces", active: true },
        ]}
      />

      <ContextBar />

      <Card className="mb-6">
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Target (Canonical)
              </label>
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <Layers className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  SmartStore Canonical Model
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Source Marketplace
              </label>
              <div className="relative">
                <select
                  data-testid="source-selector"
                  value={sourceMarketplace}
                  onChange={(e) => setSourceMarketplace(e.target.value)}
                  className={cn(
                    "w-full appearance-none pl-3 pr-8 py-2.5 rounded-lg text-sm",
                    "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600",
                    "text-slate-900 dark:text-slate-100",
                    "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                  )}
                >
                  {marketplaces.map((marketplace) => (
                    <option key={marketplace.id} value={marketplace.id}>
                      {marketplace.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <Button
              data-testid="compare-btn"
              onClick={handleCompare}
              disabled={!sourceMarketplace}
            >
              <GitCompare className="w-4 h-4" />
              Compare
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Supported Categories",
            value: String(summary.supportedCategories),
            icon: Layers,
            color: "indigo",
          },
          {
            label: "Active Categories",
            value: String(summary.activeCategories),
            icon: CheckCircle2,
            color: "emerald",
          },
          {
            label: "Mapped Fields",
            value: String(summary.mappedFields),
            icon: GitCompare,
            color: "blue",
          },
          {
            label: "Low Confidence Items",
            value: String(summary.lowConfidenceItems),
            icon: AlertTriangle,
            color: "amber",
          },
        ].map((s, i) => {
          const iconBg: Record<string, string> = {
            indigo:
              "bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400",
            emerald:
              "bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400",
            blue: "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400",
            amber:
              "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400",
          };
          const Icon = s.icon;
          return (
            <Card key={i}>
              <CardContent className="flex items-center gap-4">
                <div className={cn("p-3 rounded-lg", iconBg[s.color])}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {s.label}
                  </p>
                  <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    {s.value}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Comparison Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-md font-medium transition-colors",
                  activeTab === t.key
                    ? "bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {isError && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">
              Could not load marketplace comparison data.
            </div>
          )}

          {activeTab === "category" &&
            (categoryRows.length > 0 ? (
              <ComparisonTable
                data={categoryRows}
                type="category"
                onReview={handleReview}
                onEdit={handleEdit}
              />
            ) : (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {isLoading
                  ? "Loading supported categories..."
                  : "No supported categories found for this marketplace."}
              </div>
            ))}
          {activeTab === "attribute" && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Attribute coverage will appear here after the comparison API
              exposes field-level coverage.
            </div>
          )}
          {activeTab === "sku" && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Product coverage will appear here after SKU matching data is
              connected.
            </div>
          )}
          {activeTab === "health" && (
            <div className="space-y-3">
              {[
                summary.supportedCategories > 0
                  ? {
                      id: "active-mappings",
                      severity: "low",
                      title: "Active mappings available",
                      description: `${summary.supportedCategories} supported categories are available for ${sourceMarketplace}.`,
                    }
                  : {
                      id: "no-mappings",
                      severity: "high",
                      title: "No supported mappings found",
                      description:
                        "Approve product type mappings before this marketplace can be compared.",
                    },
              ].map((issue) => (
                <div
                  key={issue.id}
                  className={cn(
                    "p-3 rounded-lg border flex items-start gap-3",
                    issue.severity === "high"
                      ? "border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30"
                      : issue.severity === "medium"
                        ? "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900",
                  )}
                >
                  {issue.severity === "high" ? (
                    <XCircle className="w-5 h-5 text-rose-500 mt-0.5" />
                  ) : issue.severity === "medium" ? (
                    <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {issue.title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {issue.description}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReview(issue)}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
